
import { A2AOrchestrationPlan, A2AAgent, A2AMessage } from '../../types';
import { availableAgents } from '../../registry/agentRegistry';
import { 
  determineOptimalPattern, 
  buildSequentialAgentSequence,
  buildParallelAgentSequence,
  buildHierarchicalAgentSequence,
  estimateCompletionTime
} from './patterns';
import { recordOrchestrationResult } from '../../learning/adaptiveOrchestration';
import { getAgentConnectionNetwork } from '../../communication/peerToPeer';
import { adaptOrchestrationInRealTime, analyzeOrchestrationResults } from "./analysis";

const adaptiveOrchestrationPlans: A2AOrchestrationPlan[] = [];

/**
 * Creates an adaptive orchestration plan based on task complexity and required capabilities
 */
export function createAdaptiveOrchestrationPlan(
  goal: string,
  requiredCapabilities: string[],
  taskComplexity: 'simple' | 'medium' | 'complex' = 'medium'
): A2AOrchestrationPlan {
  console.log(`Creating adaptive orchestration plan for goal: ${goal}`);
  console.log(`Required capabilities: ${requiredCapabilities.join(', ')}`);
  console.log(`Task complexity: ${taskComplexity}`);
  
  // Determine the best orchestration pattern based on historical performance
  const orchestrationPattern = determineOptimalPattern(requiredCapabilities, taskComplexity);
  console.log(`Selected orchestration pattern: ${orchestrationPattern}`);
  
  // Find relevant agents
  const relevantAgents = availableAgents.filter(agent => 
    agent.isAvailable && 
    requiredCapabilities.some(cap => agent.capabilities.includes(cap))
  );
  
  // Get peer connection network to find agents that work well together
  const connectionNetwork = getAgentConnectionNetwork();
  
  let agentSequence: string[] = [];
  
  // Build agent sequence based on the selected pattern
  switch (orchestrationPattern) {
    case 'sequential':
      agentSequence = buildSequentialAgentSequence(relevantAgents, requiredCapabilities);
      break;
    case 'parallel':
      agentSequence = buildParallelAgentSequence(relevantAgents, requiredCapabilities);
      break;
    case 'hierarchical':
      agentSequence = buildHierarchicalAgentSequence(relevantAgents, requiredCapabilities);
      break;
  }
  
  const plan: A2AOrchestrationPlan = {
    id: `plan-${Date.now()}`,
    goal,
    requiredCapabilities,
    agentSequence,
    createdAt: new Date(),
    status: 'planned',
    metadata: {
      pattern: orchestrationPattern,
      complexity: taskComplexity,
      agentCount: agentSequence.length,
      estimatedCompletionTime: estimateCompletionTime(orchestrationPattern, agentSequence.length)
    }
  };
  
  adaptiveOrchestrationPlans.push(plan);
  console.log(`Created orchestration plan ${plan.id} with ${agentSequence.length} agents`);
  
  return plan;
}

/**
 * Updates the status and results of an orchestration plan
 */
export function updateAdaptivePlanStatus(
  planId: string, 
  status: 'planned' | 'in-progress' | 'completed' | 'failed',
  results?: Record<string, any>
): boolean {
  const plan = adaptiveOrchestrationPlans.find(p => p.id === planId);
  if (!plan) return false;
  
  const startTime = plan.createdAt.getTime();
  const endTime = new Date().getTime();
  const executionTime = endTime - startTime;
  
  // Record results for learning
  const wasSuccessful = status === 'completed';
  if (status === 'completed' || status === 'failed') {
    recordOrchestrationResult(
      planId,
      plan.metadata?.pattern as any || 'sequential',
      wasSuccessful,
      executionTime
    );
  }
  
  // Update plan status
  plan.status = status;
  
  if (status === 'completed' || status === 'failed') {
    plan.completedAt = new Date();
    if (results) {
      plan.results = results;
    }
  }
  
  return true;
}

/**
 * Gets all active orchestration plans
 */
export function getActivePlans(): A2AOrchestrationPlan[] {
  return [...adaptiveOrchestrationPlans];
}

/**
 * Gets a specific plan by ID
 */
export function getAdaptiveOrchestrationPlan(planId: string): A2AOrchestrationPlan | undefined {
  return adaptiveOrchestrationPlans.find(plan => plan.id === planId);
}

export function getAdaptiveOrchestrationPlans(): A2AOrchestrationPlan[] {
  return [...adaptiveOrchestrationPlans];
}

// analysis and adaptation are now imported and exported via this file.
export { analyzeOrchestrationResults, adaptOrchestrationInRealTime };
